import { computed, reactive, ref } from 'vue'
import cloneDeep from 'lodash.clonedeep'

export default (service) => {
  const profile = ref(null)
  const mode = ref(null)
  const working = ref(false)
  const apiErrors = ref([]) // backend validation report

  return {
    /**
     * Return the current drilled down account
     * @returns {*}
     */
    getAccount() {
      return service.account
    },

    /**
     * Return the current drilled down agency
     * @returns {*}
     */
    getAgency() {
      return service.agency
    },

    /**
     * Get working state
     * @returns {ComputedRef<*>}
     */
    get working() {
      return computed(() => working.value)
    },

    /**
     * Determine if a profile is under management
     * @returns {ComputedRef<*>}
     */
    get active() {
      return computed(
        () =>
          profile.value !== null &&
          ['create', 'post-create', 'update'].includes(mode.value),
      )
    },

    /**
     * Return the value of the profile
     * @returns {ComputedRef<*>}
     */
    get profile() {
      return profile.value
    },

    /**
     * Return the current mode of operation
     * @returns {ComputedRef<*>}
     */
    get mode() {
      return computed(() => mode.value)
    },

    /**
     * Return a profile by the given identifier
     * @param {String} id
     * @returns {*}
     */
    entryByID(id) {
      return service.outboundProfiles.value.find(
        (profile) => profile._id === id,
      )
    },

    /**
     * Initialise the service to handle the creation of a new profile
     */
    initForNew() {
      const template = {
        name: '',
        host: '',
        port: 22,
        type: '',
        password: '',
      }
      profile.value = reactive(template)
      mode.value = 'create'
    },

    /**
     * Initialise the service to handle updates to an existing profile
     * @param {String} id
     */
    initForID(id) {
      const entry = this.entryByID(id)
      profile.value = reactive(cloneDeep(entry))
      mode.value = 'update'
    },

    /**
     * Initialise the service to perform a deletion of an existing profile
     * @param {String} id)
     */
    deleteByID(id) {
      const entry = this.entryByID(id)
      profile.value = reactive(cloneDeep(entry))
      mode.value = 'delete'
      this.process()
    },

    /**
     * Reset the service to the initial state
     */
    reset() {
      profile.value = null
      mode.value = null
    },

    /**
     * Patch profile data
     * @param {Object} patch
     */
    patchProfile(patch = {}) {
      Object.keys(patch).forEach((k) => (profile.value[k] = patch[k]))
    },

    /**
     * Persist the referenced outbound profile
     * @param {boolean=} withReset
     * @param {String} transitionTo
     */
    process(withReset = true, transitionTo) {
      working.value = true
      service
        .processOutboundProfile(mode.value, profile.value)
        .then((result = {}) => {
          if (result.success) {
            if (withReset) {
              this.reset()
            } else {
              profile.value = { ...result.data }
            }
            if (transitionTo) mode.value = transitionTo
          } else if (result.errors instanceof Array) {
            apiErrors.value = result.errors.slice(0) // backend validation report
          }
        })
        .catch(() => {})
        .finally(() => (working.value = false))
    },
  }
}
